/**
 * ROLE-LENS-DESIGN-SYSTEM-V1 -- RelatedHistory: the related-record list with
 * a provenance line per row and a BUILT-IN honest empty state (THE
 * EVIDENCE-ROWS LAW, FLOW_183: a derived state is not explainable unless
 * its rows are reachable in-product).
 *
 * SOURCE SCREEN (extraction register): the Lead 360 payment evidence rows --
 * frontend/src/dashboard/record/AppointmentZone.tsx PaymentEvidenceLine
 * (the "- by {recordedByLabel}" provenance idiom, payrow-recorded-by) +
 * PaymentRelatedList (the related list, newest-first) + the zone's honest
 * empty state. Proof status: PROVEN live 2026-07-11 -- WALK 5 (void
 * doctrine) + WALK 6 (evidence-rows drill), founder field-proof #252
 * (golden source; the most-proven of the seven extraction sources).
 *
 * COMPETITOR FENCE: the record ACTIVITY timeline stays owned by
 * record/CondensedTimeline.tsx (registry component.condensed-timeline) --
 * RelatedHistory is the related-RECORDS list (linked evidence rows with
 * provenance), never a second timeline. Ordering is the CALLER's duty
 * (the source renders newest-first from its contract); this primitive
 * renders rows in the order given and derives nothing.
 *
 * AUDIENCE RULE (fail closed, per ui/role-lens.ts): rows render for KNOWN
 * STAFF audiences by default; per-item `audiences` narrow further
 * (LES-016: a row a role does not hold is ABSENT, never a dead door). An
 * unknown or absent audience renders the honest withheld state -- never
 * the rows.
 *
 * CLINICAL WIRE LAW (CLAUDE.md "Clinical visibility"): presentation, never
 * the security boundary. Related rows arrive from an already-lawful read;
 * server SELECT fences remain the authority. Provenance strings (actor
 * label + zone-labeled timestamp) arrive resolved from the caller -- this
 * component formats nothing (FIELDS, NOT SENTENCES stays upstream).
 */

import type { ReactNode } from 'react';
import EmptyState from './EmptyState';
import StatusChip, { type StatusChipProps } from './StatusChip';
import { audienceAllows, STAFF_AUDIENCES, type RoleLensAudience } from './role-lens';

export const RELATED_HISTORY_WITHHELD_TITLE = 'Related records not shown';
export const RELATED_HISTORY_WITHHELD_BODY =
  'Related records are limited to recognized roles and are not part of the current view.';

export interface RelatedHistoryProvenance {
  /** Actor label ("- by X" idiom from the source screen). */
  readonly byLabel?: string | null;
  /** Zone-labeled timestamp string, already formatted upstream. */
  readonly atLabel?: string | null;
  /** Source/channel label when the row carries one. */
  readonly sourceLabel?: string | null;
}

export interface RelatedHistoryItem {
  /** Stable row key. */
  readonly id: string;
  /** The row headline (e.g. "Payment #812"). */
  readonly title: string;
  /** Optional labeled short facts rendered after the title. */
  readonly facts?: readonly string[];
  /** Optional status chip (THE StatusChip canon). */
  readonly status?: {
    readonly domain: StatusChipProps['domain'];
    readonly status: string;
    readonly label?: string | null;
  };
  readonly provenance?: RelatedHistoryProvenance;
  /** Optional row door (a role-held link/control). */
  readonly action?: ReactNode;
  /** Audiences holding this row (default: staff). Absent for others. */
  readonly audiences?: readonly RoleLensAudience[];
}

export interface RelatedHistoryProps {
  /** The viewer's audience (unknown/absent -> withheld, fail closed). */
  readonly audience: RoleLensAudience | string | null | undefined;
  /** Section micro-label (e.g. "Payments" / "Prior visits"). */
  readonly title?: string;
  /** Rows in caller order (the caller owns newest-first). */
  readonly items: readonly RelatedHistoryItem[];
  /** The list's OWN empty copy (03 P11: never a generic "no data"). */
  readonly emptyTitle: string;
  readonly emptyBody?: string;
  readonly testId?: string;
}

/** THE pure row-visibility decision (pinned in tests). */
export function visibleRelatedHistoryItems(
  audience: unknown,
  items: readonly RelatedHistoryItem[],
): readonly RelatedHistoryItem[] {
  return items.filter((item) =>
    audienceAllows(audience, item.audiences ?? STAFF_AUDIENCES),
  );
}

function ProvenanceLine({
  provenance,
}: {
  provenance: RelatedHistoryProvenance;
}): JSX.Element | null {
  const parts: ReactNode[] = [];
  if (provenance.atLabel) {
    parts.push(
      <span className="ac-related__at" key="at">
        {provenance.atLabel}
      </span>,
    );
  }
  if (provenance.byLabel) {
    parts.push(
      <span className="ac-related__by" data-testid="related-provenance-by" key="by">
        {' '}
        - by {provenance.byLabel}
      </span>,
    );
  }
  if (provenance.sourceLabel) {
    parts.push(
      <span className="ac-related__source" key="source">
        {' '}
        - {provenance.sourceLabel}
      </span>,
    );
  }
  if (parts.length === 0) return null;
  return <span className="ac-related__provenance">{parts}</span>;
}

export default function RelatedHistory({
  audience,
  title,
  items,
  emptyTitle,
  emptyBody,
  testId,
}: RelatedHistoryProps): JSX.Element {
  const known = audienceAllows(audience, STAFF_AUDIENCES);
  if (!known) {
    return (
      <section className="ac-related ac-related--withheld" data-testid={testId}>
        {title ? <span className="ac-related__title">{title}</span> : null}
        <EmptyState
          title={RELATED_HISTORY_WITHHELD_TITLE}
          body={RELATED_HISTORY_WITHHELD_BODY}
        />
      </section>
    );
  }
  const visible = visibleRelatedHistoryItems(audience, items);
  return (
    <section className="ac-related" data-testid={testId}>
      {title ? <span className="ac-related__title">{title}</span> : null}
      {visible.length === 0 ? (
        <EmptyState title={emptyTitle} body={emptyBody} testId="related-empty" />
      ) : (
        <ul className="ac-related__list">
          {visible.map((item) => (
            <li className="ac-related__row" data-testid="related-row" key={item.id}>
              <span className="ac-related__headline">
                <span className="ac-related__row-title">{item.title}</span>
                {item.status ? (
                  <StatusChip
                    domain={item.status.domain}
                    status={item.status.status}
                    label={item.status.label}
                  />
                ) : null}
              </span>
              {item.facts && item.facts.length > 0 ? (
                <span className="ac-related__facts">
                  {item.facts.map((fact, index) => (
                    <span className="ac-related__fact" key={`${fact}-${index}`}>
                      {fact}
                    </span>
                  ))}
                </span>
              ) : null}
              {item.provenance ? (
                <ProvenanceLine provenance={item.provenance} />
              ) : null}
              {item.action ?? null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
